import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import {
    Pagination,
    PaginationSchema,
} from "@core/domain/models/pagination.model";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const CampaignStationSchema = z
    .object({
        id: z.number(),
        name: z.string().trim(),
    })
    .readonly();
export type CampaignStation = z.output<typeof CampaignStationSchema>;

const CampaignCropTypeSchema = z
    .object({
        id: z.number(),
        code: z.string().trim(),
    })
    .readonly();
export type CampaignCropType = z.output<typeof CampaignCropTypeSchema>;

const CampaignSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        startDate: z.string().trim(),
        endDate: z.string().trim(),
        cropType: CampaignCropTypeSchema,
        stations: z.array(CampaignStationSchema),
        notebookId: z.number(),
    })
    .brand<"Campaign">();

export type CampaignInputProps = z.input<typeof CampaignSchema>;
export type CampaignProps = z.output<typeof CampaignSchema>;

export class Campaign {
    private constructor(private readonly props: Readonly<CampaignProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get startDate(): string {
        return this.props.startDate;
    }

    get endDate(): string {
        return this.props.endDate;
    }

    get cropType(): CampaignCropType {
        return this.props.cropType;
    }

    get stations(): readonly CampaignStation[] {
        return this.props.stations;
    }

    get notebookId(): number {
        return this.props.notebookId;
    }

    static validate(
        props: Readonly<MaybeValidProps<CampaignInputProps>>,
    ): Result<CampaignProps, z.ZodError<CampaignProps>> {
        const result = CampaignSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<CampaignInputProps>>,
    ): Result<CampaignProps, PartialValid<typeof Campaign>> {
        const result = partialSafeParse(CampaignSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<CampaignInputProps>,
    ): Result<Campaign, z.ZodError<CampaignProps>> {
        return Campaign.validate(props).map(
            (validatedProps) => new Campaign(validatedProps),
        );
    }

    static createWithRecovery(
        props: CampaignInputProps,
    ): Result<Campaign, ModelError<typeof Campaign>> {
        return Campaign.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<CampaignProps> = {
                    id: error.validProps.id,
                    name: "",
                    startDate: "",
                    endDate: "",
                    cropType: {
                        id: 0,
                        code: "",
                    },
                    stations: [],
                    notebookId: 0,
                };

                const recoveryProps: Unbrand<CampaignProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Campaign>(
                    new Campaign(recoveryProps as CampaignProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Campaign(validatedProps));
    }

    toCreateProps(): CampaignInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
            cropType: this.props.cropType,
            stations: this.props.stations,
            notebookId: this.props.notebookId,
        };
    }
}

const CampaignsSchema = z
    .object({
        campaigns: z.array(
            z.custom<Campaign>((val) => val instanceof Campaign, {
                error: "Invalid Campaign instance",
            }),
        ),
        pagination: PaginationSchema.transform((validProps) =>
            Pagination.create(validProps).unwrapOr(Pagination.Default()),
        ),
    })
    .brand<"Campaigns">();

export type CampaignsInputProps = z.input<typeof CampaignsSchema>;
export type CampaignsProps = z.output<typeof CampaignsSchema>;

export class Campaigns {
    private constructor(private readonly props: CampaignsProps) {}

    get campaigns(): readonly Readonly<Campaign>[] {
        return this.props.campaigns;
    }

    get pagination(): Readonly<CampaignsProps["pagination"]> {
        return this.props.pagination;
    }

    static empty(): Campaigns {
        return Campaigns.create({
            campaigns: [],
            pagination: Pagination.Default(),
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<CampaignsInputProps>>,
    ): Result<CampaignsProps, z.ZodError<CampaignsProps>> {
        const result = CampaignsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<CampaignsInputProps>,
    ): Result<Campaigns, z.ZodError<CampaignsProps>> {
        return Campaigns.validate(props).map(
            (validatedProps) => new Campaigns(validatedProps),
        );
    }

    updatePagination(
        page: number,
        perPage: number,
    ): Result<Campaigns, z.ZodError<CampaignsProps>> {
        return Campaigns.create({
            campaigns: this.props.campaigns,
            pagination: {
                current: page,
                perPage: perPage,
                count: this.props.pagination.count,
                pages: this.props.pagination.pages,
            },
        });
    }
}

export interface CampaignFilter {
    name?: string;
    stationId?: number[];
    cropTypeId?: number;
    date?: string;
}

export const CampaignCreateSchema = z.object({
    name: z.string().trim().min(1),
    startDate: z.string().trim(),
    endDate: z.string().trim(),
    cropTypeId: z.number().min(1),
    stationIds: z.array(z.number().positive()).nonempty(),
});

export type CampaignCreateInputProps = z.input<typeof CampaignCreateSchema>;
export type CampaignCreate = z.output<typeof CampaignCreateSchema>;

export const CampaignUpdateSchema = z.object({
    id: z.number().positive(),
    name: z.string().trim().min(1),
    startDate: z.string().trim(),
    endDate: z.string().trim(),
    cropTypeId: z.number().min(1),
    stationIds: z.array(z.number().positive()).nonempty(),
});

export type CampaignUpdate = z.output<typeof CampaignUpdateSchema>;
